import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { SEOHead } from '../components/seo/SEOHead';
import { Phone, MapPin, Send, CheckCircle, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
    honeypot: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [errors, setErrors] = useState({});

  const validate = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }
    if (!formData.message.trim()) newErrors.message = 'Message is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    
    setIsSubmitting(true);
    
    try {
      const response = await axios.post(`${API}/contact`, formData);
      
      if (response.data.blocked) {
        toast.error(response.data.message || 'Unable to process your request.');
      } else {
        setIsSubmitted(true);
        toast.success('Message sent successfully!');
      }
    } catch (error) {
      console.error('Submission error:', error);
      toast.error('An error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <>
        <SEOHead page="contact" noIndex={true} />
        <div data-testid="contact-page">
          <section className="gov-section">
            <div className="max-w-2xl mx-auto px-4 md:px-6 text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="h-8 w-8 text-green-600" />
              </div>
              <h1 className="gov-section-title">Message Sent</h1>
              <p className="gov-subtitle mb-8">
                Thank you for contacting us. We will respond to your inquiry as soon as possible.
              </p>
              <Link to="/">
                <Button className="gov-btn-primary">Return Home</Button>
              </Link>
            </div>
          </section>
        </div>
      </>
    );
  }

  return (
    <>
      <SEOHead page="contact" />
      
      <div data-testid="contact-page">
        {/* Hero Section */}
      <section className="bg-[hsl(215,50%,23%)] text-white py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="max-w-3xl">
            <p className="text-white/70 text-sm uppercase tracking-wide mb-2">Get In Touch</p>
            <h1 className="text-3xl md:text-4xl font-bold mb-4 text-white">Contact Us</h1>
            <p className="text-white/80 text-lg">
              Have questions about our services? We're here to help. 
              Reach out using the form below or contact us directly.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Content */}
      <section className="gov-section bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Info */}
            <div>
              <h2 className="gov-section-title mb-8">Contact Information</h2>
              
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[hsl(210,20%,96%)] rounded-sm flex items-center justify-center flex-shrink-0">
                    <MapPin className="h-5 w-5 text-[hsl(215,50%,23%)]" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[hsl(215,50%,23%)] mb-1">Office Location</h3>
                    <p className="text-gray-600 text-sm">
                      5000 Yonge St. Suite 1901<br />
                      North York, ON M2N 7E9<br />
                      Canada
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[hsl(210,20%,96%)] rounded-sm flex items-center justify-center flex-shrink-0">
                    <Phone className="h-5 w-5 text-[hsl(215,50%,23%)]" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[hsl(215,50%,23%)] mb-1">Telephone</h3>
                    <p className="text-gray-600 text-sm">(866) 554-3535</p>
                  </div>
                </div>
                
              </div>

              <div className="mt-8 gov-panel">
                <h3 className="font-semibold text-[hsl(215,50%,23%)] mb-3">Business Hours</h3>
                <div className="space-y-2 text-gray-600 text-sm">
                  <p>Monday - Friday: 9:00 AM - 5:00 PM EST</p>
                  <p>Saturday - Sunday: Closed</p>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div>
              <div className="gov-panel">
                <h2 className="text-xl font-bold text-[hsl(215,50%,23%)] mb-6">Send a Message</h2>
                
                <form onSubmit={handleSubmit} className="space-y-4">
                  {/* Hidden honeypot */}
                  <input
                    type="text"
                    name="website"
                    value={formData.honeypot}
                    onChange={(e) => setFormData({ ...formData, honeypot: e.target.value })}
                    style={{ display: 'none' }}
                    tabIndex={-1}
                    autoComplete="off"
                  />
                  
                  <div className="space-y-2">
                    <Label htmlFor="name" className="gov-label">Full Name</Label>
                    <Input
                      id="name"
                      type="text"
                      placeholder="Enter your name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="gov-input"
                      data-testid="contact-input-name"
                    />
                    {errors.name && (
                      <p className="text-red-600 text-xs flex items-center gap-1">
                        <AlertCircle className="h-3 w-3" />
                        {errors.name}
                      </p>
                    )}
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="email" className="gov-label">Email Address</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="your@email.com"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="gov-input"
                      data-testid="contact-input-email"
                    />
                    {errors.email && (
                      <p className="text-red-600 text-xs flex items-center gap-1">
                        <AlertCircle className="h-3 w-3" />
                        {errors.email}
                      </p>
                    )}
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="phone" className="gov-label">Phone Number (Optional)</Label>
                    <Input
                      id="phone"
                      type="tel"
                      placeholder="(555) 123-4567"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="gov-input"
                      data-testid="contact-input-phone"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="message" className="gov-label">Message</Label>
                    <Textarea
                      id="message"
                      placeholder="How can we help you?"
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      className="gov-input min-h-[120px] resize-none"
                      data-testid="contact-input-message"
                    />
                    {errors.message && (
                      <p className="text-red-600 text-xs flex items-center gap-1">
                        <AlertCircle className="h-3 w-3" />
                        {errors.message}
                      </p>
                    )}
                  </div>
                  
                  <Button 
                    type="submit" 
                    disabled={isSubmitting}
                    className="gov-btn-primary w-full"
                    data-testid="contact-submit-btn"
                  >
                    {isSubmitting ? 'Sending...' : 'Send Message'}
                    <Send className="ml-2 h-4 w-4" />
                  </Button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>
      </div>
    </>
  );
}
